import React, { useMemo, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useBot } from '../../context/BotContext';
import { botApi, isBotConfigured } from '../../services/botApi';
import ReactECharts from 'echarts-for-react';

const { FiPieChart, FiActivity, FiTarget, FiAward, FiAlertTriangle, FiBarChart2, FiArrowUpRight, FiArrowDownRight, FiTrendingUp, FiDatabase, FiZap, FiLink } = FiIcons;

const StatCard = ({ title, value, subtext, icon, colorClass, highlightClass }) => (
  <div className="bg-[#1c1c1d] border border-[#2c2c2e] rounded-xl p-4 flex items-start gap-3">
    <div className={`p-2 rounded-lg ${highlightClass} shrink-0`}>
      <SafeIcon icon={icon} className={`text-lg ${colorClass}`} />
    </div>
    <div className="w-full">
      <h3 className="text-gray-500 text-[10px] uppercase tracking-wider font-semibold mb-1">{title}</h3>
      <div className={`text-lg font-bold font-mono truncate ${colorClass}`}>{value}</div>
      {subtext && <div className="text-[10px] text-gray-500 mt-1">{subtext}</div>}
    </div>
  </div>
);

const TradeRow = ({ trade, isWinner }) => (
  <div className="flex justify-between items-center p-3 bg-[#0a0a0b] border border-[#2c2c2e] rounded-lg mb-2">
    <div>
      <div className="flex items-center gap-2">
        <span className="text-white font-bold text-sm">${trade.symbol}</span>
        <span className="text-gray-500 text-[10px]">{new Date(trade.closedAt).toLocaleDateString()}</span>
      </div>
      <div className="text-[10px] text-gray-400 mt-0.5">{trade.reason}</div>
    </div>
    <div className="text-right">
      <div className={`font-bold font-mono text-sm ${isWinner ? 'text-green-400' : 'text-red-400'}`}>
        {isWinner ? '+' : ''}{trade.pnlSol.toFixed(4)} SOL
      </div>
      <div className={`text-[10px] font-mono ${isWinner ? 'text-green-500/70' : 'text-red-500/70'}`}>
        {isWinner ? '+' : ''}{trade.pnlPercentage.toFixed(2)}%
      </div>
    </div>
  </div>
);

// Live mode: real on-chain equity = burner wallet SOL (from the backend) + value of live positions.
function LiveEquitySummary({ positions }) {
  const [walletSol, setWalletSol] = useState(null);

  useEffect(() => {
    if (!isBotConfigured()) return;
    let active = true;
    const fetchBal = async () => {
      try { const w = await botApi.getWallet(); if (active) setWalletSol(w.sol); } catch (_e) { /* ignore */ }
    };
    fetchBal();
    const id = setInterval(fetchBal, 15000);
    return () => { active = false; clearInterval(id); };
  }, []);

  if (!isBotConfigured()) {
    return (
      <div className="bg-[#1c1c1d] border border-purple-500/30 rounded-2xl p-5 mb-4 shadow-[0_0_20px_rgba(168,85,247,0.05)] relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-1 bg-purple-500"></div>
        <h3 className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-3 flex items-center gap-2">
          <SafeIcon icon={FiZap} className="text-purple-400" /> Live Wallet Equity
        </h3>
        <div className="flex items-center gap-2 text-gray-400 text-sm">
          <SafeIcon icon={FiLink} className="text-purple-400" />
          Backend bot not configured — set VITE_BOT_API_URL to view live wallet analytics.
        </div>
      </div>
    );
  }

  const livePositions = positions.filter(p => p.isReal);
  const positionsValue = livePositions.reduce(
    (acc, p) => acc + (p.buyPrice > 0 ? p.amountSol * (p.currentPrice / p.buyPrice) : 0), 0
  );
  const totalEquity = (walletSol ?? 0) + positionsValue;

  return (
    <div className="bg-[#1c1c1d] border border-purple-500/30 rounded-2xl p-5 mb-4 shadow-[0_0_20px_rgba(168,85,247,0.05)] relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-purple-500 to-green-500"></div>
      <h3 className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-3 flex items-center gap-2">
        <SafeIcon icon={FiZap} className="text-purple-400" /> Live Wallet Equity
        <span className="flex items-center gap-1 bg-green-500/20 border border-green-500/40 text-green-400 text-[8px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider">
          <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" /> Live
        </span>
      </h3>
      <div className="flex justify-between items-end">
        <div>
          <div className="text-3xl font-black font-mono text-white flex items-center gap-2">
            {walletSol === null ? '—' : totalEquity.toFixed(3)} <span className="text-lg text-gray-500">SOL</span>
          </div>
          <div className="text-xs text-gray-500 mt-1 font-mono">
            Wallet: <span className="text-green-400">{walletSol === null ? '—' : walletSol.toFixed(3)}</span> | In Positions: <span className="text-purple-400">{positionsValue.toFixed(3)}</span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-white font-bold text-lg">{livePositions.length}</div>
          <div className="text-gray-500 text-[10px] uppercase">Live Positions</div>
        </div>
      </div>
    </div>
  );
}

export default function AnalyticsView() {
  const { closedTrades: paperClosed, balance, positions: paperPositions, settings, livePositions, liveTrades } = useBot();
  const isLive = settings.tradingMode === 'live';

  // Mode-aware: live analytics use Supabase live data; paper uses localStorage.
  const positions = isLive ? livePositions : paperPositions;
  const closedTrades = isLive ? liveTrades : paperClosed;

  const unrealizedValue = positions.reduce((acc, p) => acc + (p.amountSol * (p.currentPrice / p.buyPrice)), 0);
  const totalEquity = balance + unrealizedValue;
  const totalInvested = positions.reduce((acc, p) => acc + p.amountSol, 0);

  const stats = useMemo(() => {
    if (!closedTrades || closedTrades.length === 0) return null;

    const totalTrades = closedTrades.length;
    const wins = closedTrades.filter(t => t.pnlSol >= 0);
    const losses = closedTrades.filter(t => t.pnlSol < 0);
    
    const winRate = (wins.length / totalTrades) * 100;
    const totalPnlSol = closedTrades.reduce((acc, t) => acc + t.pnlSol, 0);
    
    const grossProfit = wins.reduce((acc, t) => acc + t.pnlSol, 0);
    const grossLoss = Math.abs(losses.reduce((acc, t) => acc + t.pnlSol, 0));
    
    const profitFactor = grossLoss === 0 ? (grossProfit > 0 ? '∞' : '0.00') : (grossProfit / grossLoss).toFixed(2);
    
    const avgWin = wins.length > 0 ? grossProfit / wins.length : 0;
    const avgLoss = losses.length > 0 ? grossLoss / losses.length : 0;

    const sortedTrades = [...closedTrades].sort((a, b) => new Date(a.closedAt) - new Date(b.closedAt));
    
    let currentPnl = 0;
    const cumulativeData = sortedTrades.map(t => {
      currentPnl += t.pnlSol;
      return currentPnl;
    });

    const individualTradeData = sortedTrades.map(t => t.pnlSol);

    const sortedByPnl = [...closedTrades].sort((a, b) => b.pnlSol - a.pnlSol);
    const topWinners = sortedByPnl.filter(t => t.pnlSol > 0).slice(0, 3);
    const topLosers = sortedByPnl.filter(t => t.pnlSol < 0).reverse().slice(0, 3);

    return {
      totalTrades, wins: wins.length, losses: losses.length, winRate,
      totalPnlSol, profitFactor, avgWin, avgLoss,
      sortedTrades, cumulativeData, individualTradeData,
      topWinners, topLosers
    };
  }, [closedTrades]);

  // Equity summary always renders even if no trades.
  // Live mode shows real wallet equity; paper mode shows the simulated account.
  const equitySummary = isLive ? (
    <LiveEquitySummary positions={positions} />
  ) : (
    <div className="bg-[#1c1c1d] border border-blue-500/30 rounded-2xl p-5 mb-4 shadow-[0_0_20px_rgba(59,130,246,0.05)] relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-1 bg-blue-500"></div>
      <h3 className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-3 flex items-center gap-2">
        <SafeIcon icon={FiDatabase} className="text-blue-400" /> Account Equity
      </h3>
      <div className="flex justify-between items-end">
        <div>
          <div className="text-3xl font-black font-mono text-white flex items-center gap-2">
            {totalEquity.toFixed(2)} <span className="text-lg text-gray-500">SOL</span>
          </div>
          <div className="text-xs text-gray-500 mt-1 font-mono">
            Avail: <span className="text-blue-400">{balance.toFixed(2)}</span> | Inv: <span className="text-purple-400">{totalInvested.toFixed(2)}</span>
          </div>
        </div>
        <div className="text-right">
          <div className={`text-sm font-bold ${totalEquity >= 100 ? 'text-green-400' : 'text-red-400'}`}>
            {totalEquity >= 100 ? '+' : ''}{(totalEquity - 100).toFixed(2)} SOL
          </div>
          <div className="text-gray-500 text-[10px] uppercase">All-Time Net</div>
        </div>
      </div>
    </div>
  );

  if (!stats) {
    return (
      <div className="p-4 relative min-h-[calc(100vh-140px)] lg:min-h-[calc(100vh-40px)]">
        <div className="flex items-center gap-2 mb-6">
          <SafeIcon icon={FiPieChart} className="text-white text-xl" />
          <h2 className="text-white font-bold text-xl">Historical Analysis</h2>
        </div>
        {equitySummary}
        <div className="bg-[#1c1c1d] border border-[#2c2c2e] rounded-2xl p-8 flex flex-col items-center justify-center text-center mt-4">
          <div className="w-16 h-16 bg-[#2c2c2e] rounded-full flex items-center justify-center mb-4">
            <SafeIcon icon={FiPieChart} className="text-2xl text-gray-500" />
          </div>
          <h2 className="text-white font-bold text-xl mb-2">No Data Yet</h2>
          <p className="text-gray-400 text-sm max-w-[250px]">
            Close some paper trades to see your performance analytics and historical breakdowns here.
          </p>
        </div>
      </div>
    );
  }

  const isOverallProfit = stats.totalPnlSol >= 0;

  const commonChartGrid = { top: 20, right: 10, bottom: 20, left: 50 };
  const commonXAxis = {
    type: 'category',
    data: stats.sortedTrades.map((_, i) => `#${i + 1}`),
    axisLine: { lineStyle: { color: '#3c3c3e' } },
    axisLabel: { color: '#6b7280', fontSize: 10 }
  };
  const commonYAxis = {
    type: 'value',
    splitLine: { lineStyle: { color: '#2c2c2e', type: 'dashed' } },
    axisLabel: { color: '#6b7280', fontSize: 10, formatter: (value) => value.toFixed(2) }
  };
  const commonTooltip = {
    trigger: 'axis',
    backgroundColor: '#1c1c1d',
    borderColor: '#3c3c3e',
    textStyle: { color: '#fff', fontSize: 12 },
    formatter: (params) => {
      const val = params[0].value;
      const trade = stats.sortedTrades[params[0].dataIndex];
      return `
        <div style="font-weight:bold;margin-bottom:4px;">${trade.symbol}</div>
        <div>PnL: <span style="color:${val >= 0 ? '#4ade80' : '#f87171'}">${val > 0 ? '+' : ''}${val.toFixed(4)} SOL</span></div>
        <div style="font-size:10px;color:#9ca3af;margin-top:2px;">${new Date(trade.closedAt).toLocaleDateString()}</div>
      `;
    }
  };

  const lineChartOptions = {
    grid: commonChartGrid, tooltip: commonTooltip, xAxis: commonXAxis, yAxis: commonYAxis,
    series: [{
      data: stats.cumulativeData, type: 'line', smooth: 0.3, symbol: 'circle', symbolSize: 6, showSymbol: false,
      lineStyle: { color: isOverallProfit ? '#4ade80' : '#f87171', width: 3 },
      itemStyle: { color: isOverallProfit ? '#4ade80' : '#f87171', borderColor: '#000', borderWidth: 2 },
      areaStyle: {
        color: {
          type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [
            { offset: 0, color: isOverallProfit ? 'rgba(74, 222, 128, 0.3)' : 'rgba(248, 113, 113, 0.3)' },
            { offset: 1, color: isOverallProfit ? 'rgba(74, 222, 128, 0)' : 'rgba(248, 113, 113, 0)' }
          ]
        }
      }
    }]
  };

  const barChartOptions = {
    grid: commonChartGrid, tooltip: commonTooltip, xAxis: commonXAxis, yAxis: commonYAxis,
    series: [{
      data: stats.individualTradeData, type: 'bar',
      itemStyle: { color: (params) => params.value >= 0 ? '#4ade80' : '#f87171', borderRadius: [2, 2, 0, 0] }
    }]
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-4 relative min-h-[calc(100vh-140px)] lg:min-h-[calc(100vh-40px)]">
      <div className="flex items-center gap-2 mb-6">
        <SafeIcon icon={FiPieChart} className="text-white text-xl" />
        <h2 className="text-white font-bold text-xl">Historical Analysis</h2>
      </div>

      {equitySummary}

      <div className="bg-[#1c1c1d] border border-[#2c2c2e] rounded-2xl p-5 mb-4 relative overflow-hidden">
        <div className={`absolute top-0 left-0 w-full h-1 ${isOverallProfit ? 'bg-green-500' : 'bg-red-500'}`}></div>
        <div className="flex justify-between items-end">
          <div>
            <h3 className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-2">Net Realized PnL</h3>
            <div className={`text-3xl font-black font-mono flex items-center gap-2 ${isOverallProfit ? 'text-green-400' : 'text-red-400'}`}>
              {isOverallProfit ? '+' : ''}{stats.totalPnlSol.toFixed(4)} <span className="text-lg">SOL</span>
            </div>
          </div>
          <div className="text-right">
            <div className="text-white font-bold text-lg">{stats.totalTrades}</div>
            <div className="text-gray-500 text-[10px] uppercase">Total Trades</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <StatCard title="Win Rate" value={`${stats.winRate.toFixed(1)}%`} subtext={`${stats.wins}W / ${stats.losses}L`} icon={FiTarget} colorClass={stats.winRate >= 50 ? 'text-blue-400' : 'text-orange-400'} highlightClass={stats.winRate >= 50 ? 'bg-blue-400/10' : 'bg-orange-400/10'} />
        <StatCard title="Profit Factor" value={stats.profitFactor} subtext="Gross Profit / Loss" icon={FiTrendingUp} colorClass={stats.profitFactor >= 1 ? 'text-green-400' : 'text-red-400'} highlightClass={stats.profitFactor >= 1 ? 'bg-green-400/10' : 'bg-red-400/10'} />
        <StatCard title="Avg Win" value={`+${stats.avgWin.toFixed(3)}`} icon={FiArrowUpRight} colorClass="text-green-400" highlightClass="bg-green-400/10" />
        <StatCard title="Avg Loss" value={`-${stats.avgLoss.toFixed(3)}`} icon={FiArrowDownRight} colorClass="text-red-400" highlightClass="bg-red-400/10" />
      </div>

      <div className="bg-[#1c1c1d] border border-[#2c2c2e] rounded-2xl p-4 mb-4">
        <h3 className="text-white text-sm font-semibold mb-2 flex items-center gap-2">
          <SafeIcon icon={FiActivity} className="text-blue-400" /> Cumulative Growth
        </h3>
        <div className="h-[200px] w-full -ml-2">
          <ReactECharts option={lineChartOptions} style={{ height: '100%', width: '100%' }} />
        </div>
      </div>

      <div className="bg-[#1c1c1d] border border-[#2c2c2e] rounded-2xl p-4 mb-6">
        <h3 className="text-white text-sm font-semibold mb-2 flex items-center gap-2">
          <SafeIcon icon={FiBarChart2} className="text-purple-400" /> Trade Distribution
        </h3>
        <div className="h-[180px] w-full -ml-2">
          <ReactECharts option={barChartOptions} style={{ height: '100%', width: '100%' }} />
        </div>
      </div>

      <div className="mb-6">
        <h3 className="text-white font-bold text-lg mb-3 flex items-center gap-2">
          <SafeIcon icon={FiAward} className="text-yellow-400" /> Notable Trades
        </h3>
        {stats.topWinners.length > 0 && (
          <div className="mb-4">
            <h4 className="text-gray-500 text-xs uppercase tracking-wider font-semibold mb-2 pl-1">Top Winners</h4>
            {stats.topWinners.map(trade => <TradeRow key={trade.id} trade={trade} isWinner={true} />)}
          </div>
        )}
        {stats.topLosers.length > 0 && (
          <div>
            <h4 className="text-gray-500 text-xs uppercase tracking-wider font-semibold mb-2 pl-1">Biggest Drawdowns</h4>
            {stats.topLosers.map(trade => <TradeRow key={trade.id} trade={trade} isWinner={false} />)}
          </div>
        )}
      </div>
    </motion.div>
  );
}